// components/auth/EmailVerification.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { verifyEmail, clearVerificationState } from '../../slices/authSlice.mjs';
import { setHeaderData } from '../../slices/headerSlice.mjs';
import {
    setPageTitle,
    setPageDescription,
    resetPageMetadata,
} from '../../slices/pageMetadataSlice.mjs';

import useIsMounted from '../../hooks/useIsMounted.mjs';

// Client-side navigation handler
const ClientSideVerificationHandler = ({ verificationSuccess, isAuthenticated }) => {
    const navigate = useNavigate();
    const [redirectTimer, setRedirectTimer] = useState(null);

    useEffect(() => {
        if (verificationSuccess && isAuthenticated) {
            // Redirect to home after 5 seconds
            const timer = setTimeout(() => {
                navigate('/');
            }, 5000);
            setRedirectTimer(timer);

            return () => {
                if (timer) clearTimeout(timer);
            };
        }
    }, [verificationSuccess, isAuthenticated, navigate]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (redirectTimer) clearTimeout(redirectTimer);
        };
    }, [redirectTimer]);

    return null;
};

const EmailVerificationContent = () => {
    const { token: paramToken } = useParams();
    const dispatch = useDispatch();
    const isMounted = useIsMounted();

    // Use token from props (SSR) or params (client-side navigation)
    const token = paramToken;

    // Also check preloaded state for token
    const verificationToken = useSelector((state) => state.auth.verificationToken);
    const finalToken = verificationToken || token;

    const { verificationLoading, verificationSuccess, verificationError, isAuthenticated, user } =
        useSelector((state) => state.auth || {});

    const [hasTriedVerification, setHasTriedVerification] = useState(false);

    // Debug logging
    useEffect(() => {
        console.log('EmailVerification - tokens available:', {
            paramToken,
            verificationToken,
            finalToken,
        });
    }, [paramToken, verificationToken, finalToken]);

    useEffect(() => {
        dispatch(setPageTitle('Verify Email - Nexus of Truth'));
        dispatch(setPageDescription('Verify your email address to activate your account.'));
        dispatch(
            setHeaderData({
                title: 'Email Verification',
                description: 'Activate your account.',
            })
        );

        return () => {
            dispatch(clearVerificationState());
            dispatch(resetPageMetadata());
        };
    }, [dispatch]);

    // Attempt verification when component mounts
    useEffect(() => {
        if (finalToken && !hasTriedVerification && !verificationLoading) {
            console.log('Attempting verification with token:', finalToken);
            setHasTriedVerification(true);
            dispatch(verifyEmail(finalToken));
        }
    }, [finalToken, dispatch, hasTriedVerification, verificationLoading]);

    // Loading state
    if (verificationLoading || (!hasTriedVerification && token)) {
        return (
            <div className="auth-container">
                <div className="auth-form-wrapper">
                    <div className="verification-loading">
                        <div className="home-loading-spinner"></div>
                        <h1>Verifying Your Email</h1>
                        <p>Please wait while we verify your email address...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (verificationSuccess) {
        return (
            <>
                {isMounted && (
                    <ClientSideVerificationHandler
                        verificationSuccess={verificationSuccess}
                        isAuthenticated={isAuthenticated}
                    />
                )}
                <div className="auth-container">
                    <div className="auth-form-wrapper">
                        <div className="success-message">
                            <div className="verification-success-icon">
                                <svg
                                    className="w-16 h-16 text-green-500 mx-auto mb-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                            </div>
                            <h1>Email Verified Successfully!</h1>
                            <p>Welcome to Nexus of Truth! Your account is now active.</p>

                            {isAuthenticated && user && (
                                <div className="user-welcome">
                                    <p>
                                        Hello,{' '}
                                        <strong>
                                            {user.firstName} {user.lastName}
                                        </strong>
                                        !
                                    </p>
                                    <p>You are now logged in and ready to explore.</p>
                                </div>
                            )}

                            <div className="verification-actions">
                                <div className="primary-actions">
                                    {isMounted ? (
                                        <>
                                            <Link to="/" className="submit-button">
                                                Explore Articles
                                            </Link>
                                            {isAuthenticated && (
                                                <Link to="/account" className="secondary-button">
                                                    Manage Account
                                                </Link>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <a href="/" className="submit-button">
                                                Explore Articles
                                            </a>
                                            {isAuthenticated && (
                                                <a href="/account" className="secondary-button">
                                                    Manage Account
                                                </a>
                                            )}
                                        </>
                                    )}
                                </div>

                                {isAuthenticated && (
                                    <p className="redirect-notice">
                                        You'll be automatically redirected to the home page in a few
                                        seconds...
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Error state
    if (verificationError) {
        return (
            <>
                <div className="auth-container">
                    <div className="auth-form-wrapper">
                        <div className="error-state">
                            <div className="verification-error-icon">
                                <svg
                                    className="w-16 h-16 text-red-500 mx-auto mb-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                            </div>
                            <h1>Verification Failed</h1>
                            <div className="error-message">{verificationError}</div>

                            <div className="error-help">
                                <h3>What you can do:</h3>
                                <ul>
                                    <li>Check if the verification link has expired</li>
                                    <li>Make sure you clicked the complete link from your email</li>
                                    <li>Try requesting a new verification email</li>
                                </ul>
                            </div>

                            <div className="error-actions">
                                {isMounted ? (
                                    <>
                                        <Link to="/signup" className="submit-button">
                                            Sign Up Again
                                        </Link>
                                        <Link to="/login" className="secondary-button">
                                            Back to Login
                                        </Link>
                                        <Link to="/" className="text-link">
                                            Go to Home
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <a href="/signup" className="submit-button">
                                            Sign Up Again
                                        </a>
                                        <a href="/login" className="secondary-button">
                                            Back to Login
                                        </a>
                                        <a href="/" className="text-link">
                                            Go to Home
                                        </a>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Default state (no token or other issues)
    return (
        <>
            <div className="auth-container">
                <div className="auth-form-wrapper">
                    <div className="error-state">
                        <h1>Invalid Verification Link</h1>
                        <div className="error-message">
                            The verification link appears to be invalid or incomplete.
                        </div>

                        <div className="error-actions">
                            {isMounted ? (
                                <>
                                    <Link to="/signup" className="submit-button">
                                        Sign Up
                                    </Link>
                                    <Link to="/login" className="secondary-button">
                                        Login
                                    </Link>
                                    <Link to="/" className="text-link">
                                        Go to Home
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <a href="/signup" className="submit-button">
                                        Sign Up
                                    </a>
                                    <a href="/login" className="secondary-button">
                                        Login
                                    </a>
                                    <a href="/" className="text-link">
                                        Go to Home
                                    </a>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EmailVerificationContent;
